import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  repeat,
  nothing,
} from 'omni-ui';
import { Om2Dropdown } from 'omni-campaign-ui';
import { pixToRem } from '../../helpers/utils.js';

OmniStyleElement.register();
Om2Dropdown.register();

/**
 * Class for Attention Calculator Module Dropdown Element
 * @extends OmniElement
 */
export class CampaignAttentionCalculatorDropdowns extends OmniElement {
  /**
   * Attention Calculator Lit Properties
   * @property {Object} schema - Values passed in from wrapper/router
   * @property {Number} currentAttentionStep - current step displayed in the wizard
   * @property {Object} attentionData - data for dropdown questions that is saved to savedData
   * @property {functions} handleChange - function passed down from {@link campaign-attention-calculator} that handles dropdown change event
   */
  static get properties() {
    return {
      schema: { type: Object },
      currentAttentionStep: { type: Number },
      attentionData: { type: Object },
      handleChange: { attribute: false },
      context: { type: Object },
    };
  }

  /**
   * Attention Calculator Dropdown module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .omni h4.title {
          margin-left: ${pixToRem(40)};
        }
        .legend-container {
          display: flex;
          justify-content: start;
          align-items: center;
          margin-left: ${pixToRem(50)};
        }
        .legend-color-dot {
          height: 1rem;
          border-radius: 50%;
          width: 1rem;
          margin-right: ${pixToRem(10)};
        }
        .legend-range-row {
          display: flex;
          align-items: center;
        }
        .question-dropdowns {
          display: flex;
          margin-left: ${pixToRem(40)};
          gap: ${pixToRem(30)};
          flex-wrap: wrap;
        }
        .question-label {
          width: ${pixToRem(301)};
        }
      `,
    ];
  }

  /**
   * @description Renders individual dropdown for a single step question
   * @param {string} stepLabel- The current step being rendered {@link currentAttentionStep}
   * @param {string} questionLabel- Label text for an individual dropdown question
   * @param {string} questionTooltip - Tooltip text for an individual dropdown question
   * @param {Object} questionOptions - Answers for each question with a label and associated score value
   */
  _renderDropdown(
    stepLabel,
    {
      question_label: questionLabel,
      question_tooltip: questionTooltip,
      question_options: questionOptions,
    },
  ) {
    return html`
      <div class="mb-5 question-dropdown">
        <label class="subtitle mr-1 mb-2 question-label"
          >${questionLabel ?? nothing}</label
        >
        <omni-tooltip>
          <omni-icon
            slot="invoker"
            class="is-size-2"
            icon-id="omni:informative:info"
          ></omni-icon>
          <div slot="content">${questionTooltip}</div>
        </omni-tooltip>
        <div>
          <om2-dropdown
            placeholder="Select Option"
            .options=${Object.keys(questionOptions).map(option => ({
              label: questionOptions[option].label,
              value: option,
            }))}
            .value=${this.attentionData?.[stepLabel]?.[questionLabel]}
            ?disabled=${this.context?.readonly}
            @change=${e => {
              e.preventDefault();
              this.handleChange(e, questionLabel, stepLabel);
            }}
          ></om2-dropdown>
        </div>
      </div>
    `;
  }

  /**
   * @description Renders all step dropdowns and step title
   * relies on the repeat directive to loop through all questions in a given step and call {@link _renderDropdown}
   * @param {number} stepNumber - The current step being rendered {@link currentAttentionStep}
   */
  _renderQuestionDropdowns(stepNumber) {
    const step = this.schema?.steps[stepNumber];
    const { step_label: stepLabel, step_questions: stepQuestions } = step;
    return html`
      <div>
        <h4 class="title mb-5 mt-6 is-4">
          ${this.currentAttentionStep + 1}. ${stepLabel}
        </h4>

        <div class="question-dropdowns">
          ${repeat(
            stepQuestions,
            question => `${stepNumber}:${question.question_label}`,
            question => this._renderDropdown(stepLabel, question),
          )}
        </div>
      </div>
    `;
  }

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    return html` <omni-style>
      <div>${this._renderQuestionDropdowns(this.currentAttentionStep)}</div>
    </omni-style>`;
  }
}

OmniElement.register(
  'campaign-attention-calculator-dropdowns',
  CampaignAttentionCalculatorDropdowns,
);
